import React, { FC, useContext, useLayoutEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import ReactDOM from 'react-dom';

import { OverlayContext } from '../Overlay/OverlayContext';
import { registerOverlay } from '../Overlay/overlayStack';
import {
  clampPosition,
  positionAtAnchor,
  positionMenuAtAnchor,
} from '../Overlay/positioning';

import { IParentPosition, IPopoverProps } from './popover.interface';
import styles from './popover.module.scss';

const VIEWPORT_EDGE_MARGIN_PX = 8;
const ELEMENT_ORIGIN_PX = 0;
const OVERLAP_ANCHOR_SELECTOR = '[data-popover-overlap-anchor]';
let nextPopoverId = 0;

const getControls = (surface: HTMLElement): HTMLElement[] =>
  Array.from(
    surface.querySelectorAll<HTMLElement>(
      'button,input,select,textarea,a[href],[tabindex]',
    ),
  ).filter(
    control =>
      !control.matches(':disabled,[aria-disabled="true"]') &&
      !control.closest('[hidden],[inert],[aria-hidden="true"]') &&
      getComputedStyle(control).display !== 'none' &&
      getComputedStyle(control).visibility !== 'hidden',
  );

const Popover: FC<IPopoverProps> = props => {
  const {
    anchorEl,
    children,
    handleClose,
    placement,
    busy = false,
    autoFocus = true,
    fitToAnchor = false,
    minWidth,
    id,
    role,
    className,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
  } = props;
  const menuPadding = placement === 'overlap' ? '0' : '0.25rem 0';
  const surfacePadding = role === 'dialog' && !fitToAnchor ? '1rem' : menuPadding;

  const [parentPosition, setParentPosition] = useState<IParentPosition>({ top: 0 });
  const [maxHeight, setMaxHeight] = useState<number>();

  const ref = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const parentId = useContext(OverlayContext);
  const [layerId] = useState<object>(() => ({}));
  const [popoverId] = useState(() => {
    nextPopoverId += 1;
    return `popover-${nextPopoverId}`;
  });
  const closeRef = useRef(handleClose);
  closeRef.current = () => {
    if (!busy) handleClose();
  };

  useLayoutEffect(() => {
    if (!anchorEl || !ref.current || !rootRef.current) return undefined;
    const attributes = ['aria-expanded', 'aria-controls', 'aria-haspopup'];
    const previous = attributes.map(attribute => anchorEl.getAttribute(attribute));
    anchorEl.setAttribute('aria-expanded', 'true');
    if (!anchorEl.hasAttribute('aria-controls'))
      anchorEl.setAttribute('aria-controls', id ?? popoverId);
    const popupRole =
      role === 'dialog' || !ref.current.querySelector('[role="menu"]')
        ? 'dialog'
        : 'menu';
    if (!anchorEl.hasAttribute('aria-haspopup'))
      anchorEl.setAttribute('aria-haspopup', popupRole);
    const release = registerOverlay({
      id: layerId,
      parentId,
      element: ref.current,
      root: rootRef.current,
      restoreTarget: anchorEl,
      initialFocus: autoFocus ? getControls(ref.current)[0] : null,
      onDismiss: () => closeRef.current(),
    });
    return () => {
      attributes.forEach((attribute, index) => {
        const value = previous[index];
        if (value === null) anchorEl.removeAttribute(attribute);
        else anchorEl.setAttribute(attribute, value);
      });
      release();
    };
  }, [anchorEl, autoFocus, layerId, parentId, id, popoverId, role]);

  useLayoutEffect(() => {
    const surface = ref.current;
    if (anchorEl && autoFocus && surface && !surface.contains(document.activeElement)) {
      const firstControl = getControls(surface)[0];
      (firstControl ?? surface).focus();
    }
  }, [anchorEl, autoFocus, children]);

  const [overlapSide, setOverlapSide] = useState<'left' | 'right'>('left');
  const [overlapVertical, setOverlapVertical] = useState<'down' | 'up'>('down');

  useLayoutEffect(() => {
    if (!anchorEl || !ref.current) {
      return undefined;
    }

    const popoverElement = ref.current;

    const updatePosition = (): void => {
      const anchorRect = anchorEl.getBoundingClientRect();
      const popoverWidth = popoverElement.offsetWidth;
      const popoverHeight = popoverElement.offsetHeight;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (placement === 'overlap') {
        const fitsLeft = anchorRect.right - popoverWidth >= VIEWPORT_EDGE_MARGIN_PX;
        const fitsRight =
          anchorRect.left + popoverWidth <= viewportWidth - VIEWPORT_EDGE_MARGIN_PX;
        const side = fitsLeft || !fitsRight ? 'left' : 'right';
        const fitsDown =
          anchorRect.top + popoverHeight <= viewportHeight - VIEWPORT_EDGE_MARGIN_PX;
        const fitsUp = anchorRect.bottom - popoverHeight >= VIEWPORT_EDGE_MARGIN_PX;
        const vertical = fitsDown || !fitsUp ? 'down' : 'up';

        if (side !== overlapSide || vertical !== overlapVertical) {
          setOverlapSide(side);
          setOverlapVertical(vertical);
          return;
        }

        const overlapAnchor = popoverElement.querySelector<HTMLElement>(
          OVERLAP_ANCHOR_SELECTOR,
        );
        const popoverRect = popoverElement.getBoundingClientRect();
        const overlapAnchorRect = overlapAnchor?.getBoundingClientRect();
        let overlapAnchorLeft =
          side === 'left' ? popoverWidth - anchorRect.width : ELEMENT_ORIGIN_PX;
        let overlapAnchorTop =
          vertical === 'up' ? popoverHeight - anchorRect.height : ELEMENT_ORIGIN_PX;

        if (overlapAnchorRect) {
          overlapAnchorLeft = overlapAnchorRect.left - popoverRect.left;
          overlapAnchorTop = overlapAnchorRect.top - popoverRect.top;
        }

        setOverlapSide(side);
        setOverlapVertical(vertical);
        setParentPosition(
          clampPosition(
            anchorRect.left - overlapAnchorLeft,
            anchorRect.top - overlapAnchorTop,
            popoverWidth,
            popoverHeight,
          ),
        );
        return;
      }

      if (fitToAnchor) {
        const next = positionMenuAtAnchor(
          anchorRect,
          popoverWidth,
          (popoverElement.firstElementChild as HTMLElement | null)?.offsetHeight ??
            popoverElement.scrollHeight,
          placement === 'bottom-end' ? 'bottom-end' : 'bottom-start',
        );
        setMaxHeight(next.maxHeight);
        setParentPosition({ left: next.left, top: next.top });
        return;
      }

      setParentPosition(
        positionAtAnchor(
          anchorRect,
          popoverWidth,
          popoverHeight,
          placement === 'bottom-end' ? 'bottom-end' : 'bottom-start',
        ),
      );
    };

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    updatePosition();

    let frameId: number | null = null;
    const observer = new ResizeObserver(() => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      // Changing max-height can resize the observed surface. Defer writes out
      // of the observer cycle, especially when a menu becomes rich content.
      frameId = requestAnimationFrame(() => {
        frameId = null;
        updatePosition();
      });
    });
    observer.observe(popoverElement);
    if (popoverElement.firstElementChild)
      observer.observe(popoverElement.firstElementChild);
    observer.observe(anchorEl);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      observer.disconnect();
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [anchorEl, fitToAnchor, overlapSide, overlapVertical, placement]);

  const popoverStyles = {
    minWidth:
      minWidth === undefined
        ? undefined
        : Math.min(
            minWidth,
            (document.documentElement.clientWidth || window.innerWidth) -
              VIEWPORT_EDGE_MARGIN_PX * 2,
          ),
    left: parentPosition.left,
    top: parentPosition.top,
    maxHeight,
    maxWidth: fitToAnchor
      ? (document.documentElement.clientWidth || window.innerWidth) -
        VIEWPORT_EDGE_MARGIN_PX * 2
      : undefined,
  };

  return (
    <>
      {Boolean(anchorEl) &&
        ReactDOM.createPortal(
          <OverlayContext.Provider value={layerId}>
            <div
              className={styles.overlay}
              ref={rootRef}
              onClick={event => {
                event.stopPropagation();
                closeRef.current();
              }}
            >
              <div
                className={classNames(styles.popover, className)}
                style={popoverStyles}
                data-overlap-side={placement === 'overlap' ? overlapSide : undefined}
                data-overlap-vertical={
                  placement === 'overlap' ? overlapVertical : undefined
                }
                onClick={e => e.stopPropagation()}
                onKeyDown={event => {
                  if (
                    role !== 'dialog' &&
                    event.key === 'Tab' &&
                    event.currentTarget.querySelector('[role="menu"]')
                  ) {
                    if (busy) event.preventDefault();
                    else closeRef.current();
                  }
                  if (role === 'dialog' && fitToAnchor && event.key === 'Tab') {
                    const controls = getControls(event.currentTarget).filter(
                      control => control.tabIndex >= 0,
                    );
                    const current = controls.indexOf(
                      document.activeElement as HTMLElement,
                    );
                    if (
                      current < 0 ||
                      (event.shiftKey ? current === 0 : current === controls.length - 1)
                    ) {
                      if (busy) event.preventDefault();
                      else closeRef.current();
                    }
                  }
                }}
                ref={ref}
                tabIndex={-1}
                id={id ?? popoverId}
                role={role}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                aria-describedby={ariaDescribedBy}
                aria-busy={busy || undefined}
              >
                <div style={{ padding: surfacePadding }}>{children}</div>
              </div>
            </div>
          </OverlayContext.Provider>,
          document.body,
        )}
    </>
  );
};

export { Popover };
